/**
 * Voice Guidance Engine
 *
 * Provides audio feedback during workouts using the Web Speech API.
 * Web-only — the native timer uses expo-speech directly.
 * Handles phase transitions, split announcements, periodic updates, and alerts.
 */

import { Platform } from 'react-native';

export interface VoiceGuidanceConfig {
  enabled?: boolean;
  volume?: number; // 0-1
  rate?: number; // 0.1-10 (1 is normal)
  pitch?: number; // 0-2 (1 is normal)
  language?: string; // e.g., 'en-US'
  periodicUpdateInterval?: number; // meters (default: 500m for cardio)
}

export class VoiceGuidanceEngine {
  private audioQueue: string[] = [];
  private speaking = false;
  private enabled: boolean;
  private volume: number;
  private rate: number;
  private pitch: number;
  private language: string;
  private periodicUpdateInterval: number;
  private destroyed = false;

  private lastPeriodicUpdate: number = 0;
  private synthesis: SpeechSynthesis | null = null;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(config: VoiceGuidanceConfig = {}) {
    this.enabled = config.enabled ?? true;
    this.volume = config.volume ?? 0.8;
    this.rate = config.rate ?? 0.95; // Slightly slower for better clarity
    this.pitch = config.pitch ?? 1.0;
    this.language = config.language ?? 'en-US';
    this.periodicUpdateInterval = config.periodicUpdateInterval ?? 500; // 500m default

    // Web Speech API — only available on web platform
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.speechSynthesis) {
      this.synthesis = window.speechSynthesis;
    } else {
      this.enabled = false;
    }
  }

  /**
   * Add announcement to queue and process
   */
  speak(text: string, priority: 'low' | 'normal' | 'high' = 'normal'): void {
    if (!this.enabled || !this.synthesis || !text || this.destroyed) return;

    // High priority announcements interrupt current speech
    if (priority === 'high' && this.speaking) {
      this.synthesis.cancel();
      this.audioQueue = []; // Clear queue
      this.speaking = false;
    }

    // Add to appropriate position in queue
    if (priority === 'high') {
      this.audioQueue.unshift(text);
    } else {
      this.audioQueue.push(text);
    }

    if (!this.speaking) {
      this.processQueue();
    }
  }

  /**
   * Process the audio queue
   */
  private processQueue(): void {
    if (this.audioQueue.length === 0 || !this.synthesis || this.destroyed) {
      this.speaking = false;
      return;
    }

    this.speaking = true;
    const text = this.audioQueue.shift()!;
    const utterance = new SpeechSynthesisUtterance(text);

    utterance.volume = this.volume;
    utterance.rate = this.rate;
    utterance.pitch = this.pitch;
    utterance.lang = this.language;

    utterance.onend = () => {
      // Small delay between announcements for clarity
      this.pendingTimeout = setTimeout(() => {
        this.pendingTimeout = null;
        this.processQueue();
      }, 200);
    };

    utterance.onerror = () => {
      this.speaking = false;
      this.processQueue(); // Try next item
    };

    this.synthesis.speak(utterance);
  }

  /**
   * Announce phase transition
   */
  announcePhaseTransition(phase: {
    phase_type: string;
    phase_order: number;
    target_pace?: string;
    target_hr_min?: number;
    target_hr_max?: number;
    target_power?: number;
    distance_meters?: number;
    duration_seconds?: number;
    instructions?: string;
  }): void {
    let announcement = `Starting ${phase.phase_type}`;

    if (phase.phase_order > 1) {
      announcement += ` ${phase.phase_order}`;
    }

    if (phase.target_pace) {
      announcement += `. Target pace: ${this.formatPaceForSpeech(phase.target_pace)}`;
    }

    if (phase.target_hr_min && phase.target_hr_max) {
      announcement += `. Heart rate zone: ${phase.target_hr_min} to ${phase.target_hr_max}`;
    } else if (phase.target_hr_min) {
      announcement += `. Target heart rate: ${phase.target_hr_min}`;
    }

    if (phase.target_power) {
      announcement += `. Target power: ${phase.target_power} watts`;
    }

    if (phase.distance_meters) {
      const km = phase.distance_meters / 1000;
      announcement += `. Distance: ${km.toFixed(1)} kilometers`;
    } else if (phase.duration_seconds) {
      announcement += `. Duration: ${this.formatDurationForSpeech(phase.duration_seconds)}`;
    }

    this.speak(announcement, 'normal');
  }

  /**
   * Announce split
   */
  announceSplit(km: number, time: number, pace: number, targetPace?: number): void {
    let announcement = `${km} kilometer`;

    announcement += `. Time: ${this.formatDurationForSpeech(time)}`;

    const paceMinutes = Math.floor(pace / 60);
    const paceSeconds = Math.floor(pace % 60);
    announcement += `. Pace: ${paceMinutes} minute${paceMinutes !== 1 ? 's' : ''} ${paceSeconds} second${paceSeconds !== 1 ? 's' : ''} per kilometer`;

    if (targetPace) {
      const difference = pace - targetPace;
      const absDiff = Math.abs(difference);

      if (absDiff <= 3) {
        announcement += `. On target`;
      } else if (difference < 0) {
        announcement += `. ${Math.floor(absDiff)} seconds faster than target`;
      } else {
        announcement += `. ${Math.floor(absDiff)} seconds slower than target`;
      }
    }

    this.speak(announcement, 'normal');
  }

  /**
   * Announce periodic update (e.g., every 500m)
   */
  announcePeriodicUpdate(distance: number, pace: number, heartRate?: number): void {
    // Check if enough distance has passed (meters)
    if (distance - this.lastPeriodicUpdate < this.periodicUpdateInterval) {
      return;
    }

    this.lastPeriodicUpdate = distance;

    // Handle numeric pace (seconds/km) by converting to min:sec
    const paceStr = typeof pace === 'number' && pace > 0
      ? `${Math.floor(pace / 60)}:${String(Math.floor(pace % 60)).padStart(2, '0')}`
      : `${pace}`;

    let announcement = `Current pace: ${this.formatPaceForSpeech(paceStr)}`;

    if (heartRate) {
      announcement += `. Heart rate: ${heartRate}`;
    }

    this.speak(announcement, 'low');
  }

  /**
   * Announce heart rate warning
   */
  announceHeartRateWarning(currentHR: number, maxHR: number, zone: number): void {
    const announcement = `Heart rate ${currentHR}, approaching zone ${zone} maximum`;
    this.speak(announcement, 'high');
  }

  /**
   * Announce milestone
   */
  announceMilestone(message: string): void {
    this.speak(message, 'normal');
  }

  /**
   * Format pace for speech (e.g., "4:30" -> "4 minutes 30 seconds per kilometer")
   */
  private formatPaceForSpeech(pace: string | number): string {
    const paceStr = typeof pace === 'string' ? pace : pace.toString();
    const parts = paceStr.split(':');
    if (parts.length !== 2) return paceStr;
    const min = parseInt(parts[0], 10);
    const sec = parseInt(parts[1], 10);

    if (isNaN(min)) return paceStr;

    let result = `${min} minute${min !== 1 ? 's' : ''}`;
    if (!isNaN(sec) && sec > 0) {
      result += ` ${sec} second${sec !== 1 ? 's' : ''}`;
    }
    result += ' per kilometer';

    return result;
  }

  /**
   * Format duration for speech (seconds to natural language)
   */
  private formatDurationForSpeech(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const parts: string[] = [];

    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
    }
    if (seconds > 0 && hours === 0) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);
    }

    return parts.join(' ');
  }

  /**
   * Enable/disable voice guidance
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (!enabled) {
      this.synthesis?.cancel();
      this.audioQueue = [];
      this.speaking = false;
      if (this.pendingTimeout) { clearTimeout(this.pendingTimeout); this.pendingTimeout = null; }
    }
  }

  /**
   * Set volume
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Set speech rate
   */
  setRate(rate: number): void {
    this.rate = Math.max(0.1, Math.min(10, rate));
  }

  /**
   * Get enabled state
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Clear queue and stop speaking
   */
  stopAll(): void {
    this.synthesis?.cancel();
    this.audioQueue = [];
    this.speaking = false;
    if (this.pendingTimeout) { clearTimeout(this.pendingTimeout); this.pendingTimeout = null; }
  }

  /**
   * Full teardown — call on component unmount. Prevents dangling timeouts.
   */
  destroy(): void {
    this.stopAll();
    this.destroyed = true;
    this.synthesis = null;
  }

  /**
   * Test voice (useful for settings)
   */
  test(): void {
    this.speak('Voice guidance is working', 'normal');
  }
}
